import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { aptRequestAction, updateAptLine } from 'api/apartmentProcurement'

import { FULFILMENT_META, RECEIPT_META, STATUS_META, money } from './constants'
import { RoleContext } from './roleContext'

// Read view of one request + the actions the caller's role allows at its
// current stage. Receipt confirmation (per line) lives here; HQ's fulfilment
// editing lives in the HQ queue (Procurement & Expenses -> At Apartment).
const useStyles = makeStyles((theme) => ({
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  reason: {
    background: '#fff3f3',
    padding: theme.spacing(1),
    borderRadius: 4,
    marginBottom: theme.spacing(1)
  }
}))

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: '#999' }
  return (
    <Chip
      size="small"
      label={meta.label}
      style={{ background: meta.color, color: '#fff' }}
    />
  )
}

const RequestDetail = ({ request, onClose, onChanged, onEdit }) => {
  const classes = useStyles()
  const role = React.useContext(RoleContext)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [sel, setSel] = React.useState(() => new Set()) // bulk receipt selection

  if (!request) return null
  const r = request

  const act = async (action, reason) => {
    setBusy(true)
    setError('')
    try {
      await aptRequestAction(r.uuid, action, reason)
      onChanged()
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.detail) ||
          'Action failed.'
      )
    } finally {
      setBusy(false)
    }
  }

  const setReceipt = async (line, receipt_status) => {
    setBusy(true)
    try {
      await updateAptLine({ uuid: line.uuid, receipt_status })
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  const setReceiptNote = async (line, receipt_note) => {
    await updateAptLine({ uuid: line.uuid, receipt_note })
  }

  const reject = () => {
    const reason = window.prompt('Reason for rejecting this request:')
    if (reason && reason.trim()) act('reject', reason.trim())
  }

  const isDraft = r.status === 'draft' || r.status === 'rejected'
  const canSubmit = isDraft && (role.can_request || role.is_central)
  const canClusterApprove = r.status === 'submitted'
  // The At Apartment head can final-approve straight from submitted, skipping
  // the cluster manager (P1) — they don't have to wait for the cluster stage.
  const canOverrideFinal = r.status === 'submitted' && role.is_central
  const canFinalApprove = r.status === 'cluster_approved' && role.is_central
  // An At Apartment HQ (store) order has no apartment to hand over / confirm
  // receipt at — "received by HQ" just means the goods reached the HQ store, i.e.
  // the line is delivered, which stocks it into HQ inventory (2026-07-28).
  const isHqStore = /(^|\W)at apartment hq(\W|$)/i.test(r.apartment_name || '')
  // Receipt also opens while the order is still on HQ's daily sheet
  // (2026-08-31): goods reach the apartment before anyone clicks hand over —
  // waiting on that click made acceptance unreachable in practice. Closing
  // still happens at handed_over only.
  const receiptOpen =
    r.status === 'handed_over' || (!isHqStore && r.status === 'in_daily')
  const closeOpen = r.status === 'handed_over'
  const allReceiptSet =
    receiptOpen && (r.lines || []).every((l) => l.receipt_status !== 'pending')
  const fulfilmentOpen =
    isHqStore &&
    ['final_approved', 'in_daily', 'handed_over'].includes(r.status)

  // Bulk actions in the popup — tick lines (or all) and set them at once instead
  // of one dropdown at a time (2026-07-28). Receipt for real apartments once
  // handed over; "received by HQ" (delivered) for the HQ store.
  const selectLines = receiptOpen
    ? r.lines || []
    : fulfilmentOpen
    ? r.lines || []
    : []
  const receiptLines = receiptOpen ? r.lines || [] : []
  const selectedReceipt = receiptLines.filter((l) => sel.has(l.uuid))
  const selectedLines = selectLines.filter((l) => sel.has(l.uuid))
  const allSelected =
    selectLines.length > 0 && selectLines.every((l) => sel.has(l.uuid))
  const allDeliveredHq =
    fulfilmentOpen && (r.lines || []).every((l) => l.fulfilment_status === 'delivered')
  const toggleSel = (uuid) =>
    setSel((p) => {
      const n = new Set(p)
      if (n.has(uuid)) n.delete(uuid)
      else n.add(uuid)
      return n
    })
  const toggleAllReceipt = () =>
    setSel(allSelected ? new Set() : new Set(selectLines.map((l) => l.uuid)))
  const bulkReceipt = async (status, lines) => {
    const targets = lines || selectedReceipt
    if (!targets.length) return
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const l of targets) {
        // eslint-disable-next-line no-await-in-loop
        await updateAptLine({ uuid: l.uuid, receipt_status: status })
      }
      setSel(new Set())
      onChanged()
    } finally {
      setBusy(false)
    }
  }
  // "Mark received by HQ" = deliver the line, which stocks it into HQ inventory.
  const bulkReceivedByHq = async (lines) => {
    const targets = lines || selectedLines
    if (!targets.length) return
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const l of targets) {
        // eslint-disable-next-line no-await-in-loop
        await updateAptLine({ uuid: l.uuid, fulfilment_status: 'delivered' })
      }
      setSel(new Set())
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {r.title || 'Procurement request'} — {r.apartment_name}
      </DialogTitle>
      <DialogContent>
        <Box className={classes.meta}>
          <StatusChip status={r.status} />
          {r.po_number && (
            <Chip
              size="small"
              label={`PO ${r.po_number}`}
              style={{ background: '#37474f', color: '#fff' }}
            />
          )}
          {r.kit_name && <Chip size="small" label={r.kit_name} />}
          {r.cluster_name && <Chip size="small" label={`${r.cluster_name} cluster`} />}
          <Typography variant="caption">
            Raised by {r.requested_by_name || '—'}
          </Typography>
          {r.submitted_at && (
            <Typography variant="caption">
              · submitted {String(r.submitted_at).slice(0, 10)}
            </Typography>
          )}
          {r.cluster_approved_at && r.cluster_approved_by_name && (
            <Typography variant="caption">
              · cluster ✓ {r.cluster_approved_by_name}
            </Typography>
          )}
          {r.final_approved_at && (
            <Typography variant="caption">
              · approved {String(r.final_approved_at).slice(0, 10)}
              {r.final_approved_by_name ? ` by ${r.final_approved_by_name}` : ''}
            </Typography>
          )}
        </Box>

        {r.status === 'rejected' && (
          <Box className={classes.reason}>
            <Typography variant="body2">
              Rejected at the {r.rejection_stage} stage: {r.rejection_reason}
            </Typography>
          </Box>
        )}
        {r.notes && (
          <Typography variant="body2" gutterBottom>
            {r.notes}
          </Typography>
        )}

        {receiptOpen && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              margin: '4px 0 8px',
              padding: 8,
              background: '#eef4ff',
              borderRadius: 4
            }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              disabled={busy || allReceiptSet}
              onClick={() =>
                bulkReceipt(
                  'received',
                  receiptLines.filter((l) => l.receipt_status === 'pending')
                )
              }>
              Mark all received
            </Button>
            {selectedReceipt.length > 0 && (
              <>
                <span style={{ borderLeft: '1px solid #c7d5ee', height: 20 }} />
                <Typography variant="body2">
                  <b>{selectedReceipt.length}</b> selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => bulkReceipt('received')}>
                  Mark received
                </Button>
                <Select
                  value=""
                  displayEmpty
                  disabled={busy}
                  onChange={(e) => e.target.value && bulkReceipt(e.target.value)}
                  style={{ fontSize: 13, minWidth: 150 }}>
                  <MenuItem value="" disabled>
                    Set selected to…
                  </MenuItem>
                  {Object.entries(RECEIPT_META).map(([value, meta]) => (
                    <MenuItem key={value} value={value}>
                      {meta.label}
                    </MenuItem>
                  ))}
                </Select>
                <Button size="small" onClick={() => setSel(new Set())}>
                  Clear
                </Button>
              </>
            )}
          </Box>
        )}

        {fulfilmentOpen && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              margin: '4px 0 8px',
              padding: 8,
              background: '#e8f5e9',
              borderRadius: 4
            }}>
            <Typography variant="body2" style={{ marginRight: 4 }}>
              Ordered for the HQ store —{' '}
              <b>mark received by HQ</b> to stock it into inventory.
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              disabled={busy || allDeliveredHq}
              onClick={() =>
                bulkReceivedByHq(
                  (r.lines || []).filter((l) => l.fulfilment_status !== 'delivered')
                )
              }>
              Mark all received by HQ
            </Button>
            {selectedLines.length > 0 && (
              <>
                <span style={{ borderLeft: '1px solid #bcd', height: 20 }} />
                <Typography variant="body2">
                  <b>{selectedLines.length}</b> selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => bulkReceivedByHq()}>
                  Mark selected received by HQ
                </Button>
                <Button size="small" onClick={() => setSel(new Set())}>
                  Clear
                </Button>
              </>
            )}
          </Box>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              {(receiptOpen || fulfilmentOpen) && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedLines.length > 0 && !allSelected}
                    onChange={toggleAllReceipt}
                  />
                </TableCell>
              )}
              <TableCell>Item</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Sides</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Taxable</TableCell>
              <TableCell align="right">GST</TableCell>
              <TableCell align="right">Invoice</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Fulfilment</TableCell>
              {receiptOpen || r.status === 'closed' ? (
                <TableCell>Receipt</TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {(r.lines || []).map((line) => {
              const fulfilment =
                FULFILMENT_META[line.fulfilment_status] || FULFILMENT_META.pending
              return (
                <TableRow key={line.uuid}>
                  {(receiptOpen || fulfilmentOpen) && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={sel.has(line.uuid)}
                        onChange={() => toggleSel(line.uuid)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {line.reference_link ? (
                      <Link
                        href={line.reference_link}
                        target="_blank"
                        rel="noopener noreferrer">
                        {line.item_name}
                      </Link>
                    ) : (
                      line.item_name
                    )}
                    {line.segment ? (
                      <Typography variant="caption" display="block">
                        {line.segment}
                      </Typography>
                    ) : null}
                    {(line.print_specification || line.paper_kind) && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary">
                        {[line.print_specification, line.paper_kind]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                    {(line.file_link || line.invoice_link) && (
                      <Typography variant="caption" display="block">
                        {line.file_link && (
                          <Link
                            href={line.file_link}
                            target="_blank"
                            rel="noopener noreferrer">
                            print file
                          </Link>
                        )}{' '}
                        {line.invoice_link && (
                          <Link
                            href={line.invoice_link}
                            target="_blank"
                            rel="noopener noreferrer">
                            invoice PDF
                          </Link>
                        )}
                      </Typography>
                    )}
                    {(line.source_of_request || line.reference_id) && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary">
                        {[
                          line.source_of_request,
                          line.reference_id && `ref ${line.reference_id}`
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                    {line.remarks ? (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {line.remarks}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{line.category || '—'}</TableCell>
                  <TableCell align="right">{Number(line.qty)}</TableCell>
                  <TableCell align="right">
                    {line.sides_per_file
                      ? `${line.sides_per_file}×${Number(line.total_sides)}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {line.ppp
                      ? `${money(line.ppp)}/${line.item_type === 'print' ? 'side' : 'unit'}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">{money(line.line_total)}</TableCell>
                  <TableCell align="right">
                    {Number(line.gst_amount) ? money(line.gst_amount) : '—'}
                  </TableCell>
                  <TableCell align="right">{money(line.invoice_amount)}</TableCell>
                  <TableCell>{line.vendor || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={fulfilment.label}
                      style={{ background: fulfilment.color, color: '#fff' }}
                    />
                    {line.expected_delivery &&
                      line.fulfilment_status !== 'delivered' && (
                        <Typography variant="caption" display="block">
                          ETA {line.expected_delivery}
                        </Typography>
                      )}
                    {line.delivered_on && (
                      <Typography variant="caption" display="block">
                        on {line.delivered_on}
                      </Typography>
                    )}
                    {fulfilmentOpen && line.fulfilment_status !== 'delivered' && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        style={{ marginTop: 4, color: '#2e7d32', borderColor: '#2e7d32' }}
                        onClick={() => bulkReceivedByHq([line])}>
                        ✓ Received
                      </Button>
                    )}
                  </TableCell>
                  {receiptOpen ? (
                    <TableCell>
                      <Select
                        value={line.receipt_status}
                        disabled={busy}
                        onChange={(e) => setReceipt(line, e.target.value)}>
                        {Object.entries(RECEIPT_META).map(([value, meta]) => (
                          <MenuItem key={value} value={value}>
                            {meta.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {['not_received', 'discrepancy'].includes(
                        line.receipt_status
                      ) && (
                        <TextField
                          placeholder="What went wrong?"
                          defaultValue={line.receipt_note}
                          onBlur={(e) => setReceiptNote(line, e.target.value)}
                          fullWidth
                        />
                      )}
                    </TableCell>
                  ) : r.status === 'closed' ? (
                    <TableCell>
                      {(RECEIPT_META[line.receipt_status] || {}).label}
                      {line.receipt_note ? ` — ${line.receipt_note}` : ''}
                    </TableCell>
                  ) : null}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isDraft && onEdit && (
          <Button onClick={() => onEdit(r)} disabled={busy}>
            Edit lines
          </Button>
        )}
        {canSubmit && (
          <Button
            color="primary"
            variant="contained"
            disabled={busy}
            onClick={() => act('submit')}>
            Submit for approval
          </Button>
        )}
        {canClusterApprove && (
          <>
            <Button color="secondary" disabled={busy} onClick={reject}>
              Reject
            </Button>
            {canOverrideFinal && (
              <Button
                color="primary"
                disabled={busy}
                title="Approve now, skipping the cluster manager"
                onClick={() => act('final_approve')}>
                Final approve (skip cluster)
              </Button>
            )}
            <Button
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={() => act('cluster_approve')}>
              Approve (cluster)
            </Button>
          </>
        )}
        {canFinalApprove && (
          <>
            <Button color="secondary" disabled={busy} onClick={reject}>
              Reject
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={() => act('final_approve')}>
              Final approve
            </Button>
          </>
        )}
        {closeOpen && (
          <Button
            color="primary"
            variant="contained"
            disabled={busy || !allReceiptSet}
            title={
              allReceiptSet
                ? undefined
                : 'Set receipt on every line first'
            }
            onClick={() => act('close')}>
            Confirm receipt & close
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default RequestDetail
